/**
 * ChatPanel — global AI assistant, rendered as a right-side panel.
 *
 * On desktop: fills a resizable panel beside the main content.
 * On mobile: shown as a full-height sheet.
 * Supports SSE streaming, tool execution indicators, and conversation history.
 */

import {
  memo,
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react"
import { cn } from "@/shared/utils/general/cn"
import { useAiChat, type ChatAttachment, type ChatContext } from "./useAiChat"
import { ChatMessageBubble } from "./ChatMessageBubble"
import { ChatHistory } from "./ChatHistory"
import { useViewContext } from "@/app/stores/viewContext"
import { useSearchParams } from "react-router-dom"
import { useLiveQueryImmediate } from "@/db/hooks"
import { db, ObjectType } from "@/db/schema"
import CrossIcon from "@/shared/icons/totodoicons/stroke/cross.svg?react"
import SendIcon from "@/shared/icons/totodoicons/stroke/arrow-up.svg?react"
import PlusIcon from "@/shared/icons/totodoicons/stroke/plus.svg?react"
import HistoryIcon from "@/shared/icons/pikaicons/stroke/time/clock-default.svg?react"
import { IconPicker } from "@/features/icon-picker/IconPicker"
import { ContainerIcon } from "@/shared/icons/resolveContainerIcon"
import SparkleIcon from "@/shared/icons/pikaicons/stroke/general/sparkle-ai-01.svg?react"

import { useTranslation } from "react-i18next"
import { useAiQuota } from "./useAiQuota"
import { quotaChipText, quotaChipTitle, quotaNotice } from "./aiQuotaDisplay"
import { acceptAttachments, base64Bytes, CHAT_MAX_ATTACHMENTS } from "./attachmentLimits"
import { toast } from "sonner"
import { useKeyboardStore } from "@/features/auth/stores/keyboardStore"

const DEFAULT_ASSISTANT_ICON = "pika:general/sparkle-ai-01"

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ChatPanel = memo(function ChatPanel() {
  const setAiChatOpen = useViewContext((s) => s.setAiChatOpen)
  const viewId = useViewContext((s) => s.viewId)
  const containerId = useViewContext((s) => s.containerId)
  // Read activeTaskId from URL (?tid=) — the canonical source of truth
  const [searchParams] = useSearchParams()
  const activeTaskId = searchParams.get("tid")

  const chat = useAiChat()
  const keyboardOpen = useKeyboardStore((s) => s.keyboard?.isOpen ?? false)
  const { t } = useTranslation()
  const { view: quotaState, isWarning, isExhausted, refetch: refetchQuota } = useAiQuota()
  // The count changes with every message: ask again when a reply has finished.
  const wasStreaming = useRef(false)
  useEffect(() => {
    if (wasStreaming.current && !chat.isStreaming) refetchQuota()
    wasStreaming.current = chat.isStreaming
  }, [chat.isStreaming, refetchQuota])
  const quotaNoticeText = quotaState ? quotaNotice(quotaState, t) : null
  const [input, setInput] = useState("")
  const [showHistory, setShowHistory] = useState(false)
  const [attachments, setAttachments] = useState<ChatAttachment[]>([])
  const [isEditingName, setIsEditingName] = useState(false)
  const [showIconPicker, setShowIconPicker] = useState(false)
  const nameInputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const endRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Load assistant name and icon from settings
  const [assistantName] = useLiveQueryImmediate(
    () => db.setting.get("#ai-assistant-name").then((s) => (s?.value as string) || null),
    ["ai-assistant-name"],
    null,
  )
  const [assistantIcon] = useLiveQueryImmediate(
    () => db.setting.get("#ai-assistant-icon").then((s) => (s?.value as string) || null),
    ["ai-assistant-icon"],
    null,
  )

  const displayName = assistantName || "Assistant"
  const displayIcon = assistantIcon || DEFAULT_ASSISTANT_ICON

  // Look up active task and container metadata for AI context
  const [activeTask] = useLiveQueryImmediate(
    () => activeTaskId ? db.totodo.get(activeTaskId) : undefined,
    ["chat-activeTask", activeTaskId],
    undefined,
  )
  const [container] = useLiveQueryImmediate(
    () => containerId ? db.totodo.get(containerId) : undefined,
    ["chat-container", containerId],
    undefined,
  )

  // Build enriched context for AI
  const chatContext: ChatContext = useMemo(() => {
    const ctx: ChatContext = { viewId }
    if (containerId) {
      ctx.containerId = containerId
      if (container) {
        ctx.containerTitle = container.title
        ctx.containerType = container.type === ObjectType.Project ? "project"
          : container.type === ObjectType.List ? "list"
          : container.type === ObjectType.Area ? "area" : undefined
      }
    }
    if (activeTaskId) {
      ctx.activeTaskId = activeTaskId
      if (activeTask) {
        ctx.activeTaskTitle = activeTask.title
        ctx.activeTaskNote = activeTask.note || undefined
        ctx.activeTaskTags = activeTask.tags?.length ? activeTask.tags : undefined
        ctx.activeTaskDueAt = activeTask.dueAt || undefined
        ctx.activeTaskParentPath = activeTask.parentPath || undefined
      }
    }
    return ctx
  }, [viewId, containerId, container, activeTaskId, activeTask])

  // Instant scroll to bottom on mount
  const hasScrolledOnMount = useRef(false)
  useEffect(() => {
    if (!hasScrolledOnMount.current && chat.messages.length > 0) {
      hasScrolledOnMount.current = true
      endRef.current?.scrollIntoView({ behavior: "instant" })
    }
  }, [chat.messages.length])

  // Smart scroll: keep the last user message visible at top when response is long.
  // If response fits in viewport, scroll to bottom as usual.
  useEffect(() => {
    if (!hasScrolledOnMount.current) return
    const container = scrollRef.current
    if (!container) return

    // Find the last user message element
    const userMsgEls = container.querySelectorAll("[data-role='user']")
    const lastUserEl = userMsgEls[userMsgEls.length - 1] as HTMLElement | undefined

    if (lastUserEl) {
      const containerRect = container.getBoundingClientRect()
      const endElRect = endRef.current?.getBoundingClientRect()

      // If the end is visible (response fits), scroll to bottom
      if (endElRect && endElRect.bottom <= containerRect.bottom + 50) {
        endRef.current?.scrollIntoView({ behavior: "smooth" })
      } else {
        // Response is too long — keep user message at top
        const userElTop = lastUserEl.offsetTop - container.offsetTop
        container.scrollTo({ top: userElTop - 8, behavior: "smooth" })
      }
    } else {
      endRef.current?.scrollIntoView({ behavior: "smooth" })
    }
  }, [chat.messages, chat.isStreaming])

  // Focus textarea on mount
  useEffect(() => {
    setTimeout(() => textareaRef.current?.focus(), 100)
  }, [])

  // Build context hint for the placeholder
  const contextHint = activeTaskId
    ? "Ask about this task..."
    : containerId
      ? "Ask about this project..."
      : viewId === "today"
        ? "Plan your day..."
        : "Ask anything..."

  // Assistant name double-click to edit
  const handleNameDoubleClick = useCallback(() => {
    setIsEditingName(true)
    setTimeout(() => nameInputRef.current?.select(), 50)
  }, [])

  const handleNameSave = useCallback(async (value: string) => {
    const trimmed = value.trim()
    if (trimmed) {
      const now = new Date().toISOString()
      await db.setting.put({ id: "#ai-assistant-name", value: trimmed, device: "", createdAt: now, createdBy: "user" })
    } else {
      // Empty = reset to default
      await db.setting.delete("#ai-assistant-name")
    }
    setIsEditingName(false)
  }, [])

  const handleNameKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleNameSave(e.currentTarget.value)
    } else if (e.key === "Escape") {
      setIsEditingName(false)
    }
  }, [handleNameSave])

  // Icon picker
  const handleIconSelect = useCallback(async (icon: string | undefined) => {
    if (!icon) {
      await db.setting.delete("#ai-assistant-icon")
    } else {
      const now = new Date().toISOString()
      await db.setting.put({ id: "#ai-assistant-icon", value: icon, device: "", createdAt: now, createdBy: "user" })
    }
    setShowIconPicker(false)
  }, [])

  // Send handler — can send during streaming (stops current stream first)
  const handleSend = useCallback(() => {
    const trimmed = input.trim()
    if (!trimmed && attachments.length === 0) return
    if (chat.isStreaming) {
      chat.stopStreaming()
    }
    chat.sendMessage(trimmed || "Describe this", chatContext, attachments.length ? attachments : undefined)
    setInput("")
    setAttachments([])
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto"
    }
  }, [input, chat, chatContext, attachments])

  // Choice callback — when user clicks an option in disambiguation UI
  const handleChoiceMessage = useCallback((text: string) => {
    if (chat.isStreaming) return
    chat.sendMessage(text, chatContext)
  }, [chat, chatContext])

  // File upload handler
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files) return

    // The server's limits: at most 5 files and 8 MB in all per message (attachmentLimits.ts)
    const picked = Array.from(files)
    const { accepted, rejected } = acceptAttachments(
      attachments.map((a) => base64Bytes(a.data)),
      picked.map((f) => ({ name: f.name, size: f.size })),
    )
    if (rejected.length > 0) {
      const reason = rejected[0]!.reason
      toast.error(
        reason === "too_many"
          ? t("At most {{count}} files per message.", { count: CHAT_MAX_ATTACHMENTS })
          : t("Files can be at most 8 MB in all per message: {{names}}", {
              names: rejected.map((r) => r.name).join(", "),
            }),
      )
    }

    accepted.map((i) => picked[i]!).forEach((file) => {
      const reader = new FileReader()
      reader.onload = () => {
        const base64 = (reader.result as string).split(",")[1] ?? ""
        setAttachments((prev) => [
          ...prev,
          { mimeType: file.type, data: base64, name: file.name },
        ])
      }
      reader.readAsDataURL(file)
    })

    // Reset input so same file can be selected again
    e.target.value = ""
  }, [attachments, t])

  // Keyboard: Enter sends, Shift+Enter inserts newline
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
    },
    [handleSend],
  )

  // Auto-resize textarea
  const handleTextareaChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setInput(e.target.value)
      const el = e.target
      el.style.height = "auto"
      el.style.height = `${Math.min(el.scrollHeight, 120)}px`
    },
    [],
  )

  // Select a conversation from history
  const handleSelectConversation = useCallback(
    (convId: string) => {
      chat.loadConversation(convId)
      setShowHistory(false)
    },
    [chat],
  )

  // New conversation
  const handleNewConversation = useCallback(() => {
    chat.newConversation()
    setShowHistory(false)
    setTimeout(() => textareaRef.current?.focus(), 50)
  }, [chat])

  const hasMessages = chat.messages.length > 0

  return (
    <div data-ai-chat className="flex h-full flex-col border-l border-border/40 bg-background" style={{ paddingTop: "var(--status-bar-height, 0px)" }}>
      {/* Header — minimal, elegant */}
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2.5">
          {/* Icon — click to pick a new one */}
          <button
            onClick={() => setShowIconPicker(true)}
            className="flex size-7 items-center justify-center rounded-md transition-colors hover:bg-accent"
            title="Change icon"
          >
            {displayIcon !== DEFAULT_ASSISTANT_ICON ? (
              <ContainerIcon icon={displayIcon} size="1rem" />
            ) : (
              <SparkleIcon className="size-4 text-muted-foreground" />
            )}
          </button>
          <IconPicker
            open={showIconPicker}
            onOpenChange={setShowIconPicker}
            onIconSelected={handleIconSelect}
            currentIconOverride={displayIcon}
          />
          {/* Name — double-click to edit */}
          {isEditingName ? (
            <input
              ref={nameInputRef}
              defaultValue={assistantName || ""}
              placeholder="Assistant"
              className="w-24 rounded border border-border bg-background px-1.5 py-0.5 text-[13px] font-medium text-foreground/80 outline-none focus:ring-1 focus:ring-primary"
              onBlur={(e) => handleNameSave(e.target.value)}
              onKeyDown={handleNameKeyDown}
              autoFocus
            />
          ) : (
            <span
              className="cursor-default select-none text-[13px] font-medium text-foreground/80"
              onDoubleClick={handleNameDoubleClick}
              title="Double-click to rename"
            >
              {displayName}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {/* Quota indicator */}
          {quotaState && (
            <span
              className={cn(
                "mr-1 text-[11px] tabular-nums",
                isExhausted ? "text-destructive font-medium" :
                isWarning ? "text-amber-500" :
                "text-muted-foreground/60",
              )}
              title={quotaChipTitle(quotaState, t)}
            >
              {quotaChipText(quotaState, t)}
            </span>
          )}
          <button
            onClick={() => setShowHistory(!showHistory)}
            className={cn(
              "flex size-7 items-center justify-center rounded-md transition-colors hover:bg-accent",
              showHistory && "bg-accent",
            )}
            title="History"
          >
            <HistoryIcon className="size-4 text-muted-foreground" />
          </button>
          <button
            onClick={handleNewConversation}
            className="flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            title="New chat"
          >
            <PlusIcon className="size-4" />
          </button>
          <button
            onClick={() => setAiChatOpen(false)}
            className="flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            title="Close (Esc)"
          >
            <CrossIcon className="size-3.5" />
          </button>
        </div>
      </div>

      {/* Content */}
      {showHistory ? (
        <ChatHistory
          onSelect={handleSelectConversation}
          activeId={chat.conversationId}
        />
      ) : (
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-4 py-4"
        >
          {!hasMessages && (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
              <div className="flex size-10 items-center justify-center rounded-full bg-foreground/[0.04] dark:bg-foreground/[0.06]">
                <span className="text-lg">✦</span>
              </div>
              <div>
                <h3 className="text-sm font-medium text-foreground">What can I help with?</h3>
                <p className="mt-1 max-w-[220px] text-xs text-muted-foreground leading-relaxed">
                  Create tasks, manage projects, search and plan your day.
                </p>
              </div>
              <div className="mt-2 flex flex-wrap justify-center gap-1.5">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => {
                      setInput(s)
                      textareaRef.current?.focus()
                    }}
                    className="rounded-full border border-border/60 px-3 py-1.5 text-[11px] text-muted-foreground transition-all hover:border-border hover:bg-accent hover:text-foreground"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {hasMessages && (
            <div className="space-y-4">
              {chat.messages
                .filter((msg) => msg.content !== "(message omitted)" && !msg.content.startsWith("[context:"))
                .map((msg) => (
                <ChatMessageBubble key={msg.id} message={msg} onFeedback={chat.setFeedback} onTogglePin={chat.toggleMessagePin} onSendMessage={handleChoiceMessage} />
              ))}
              {/* Retry button — show when last exchange failed or got stuck */}
              {!chat.isStreaming && !chat.error && chat.messages.length >= 1 && (() => {
                const lastMsg = chat.messages[chat.messages.length - 1]
                // Orphaned user message (no AI response came back)
                if (lastMsg?.role === "user") return true
                // Empty assistant message (stream completed with no content or tool calls)
                if (lastMsg?.role === "assistant" && !lastMsg.content && !lastMsg.toolCalls?.length) return true
                // Last assistant message has tool calls that all failed
                if (lastMsg?.role === "assistant" && lastMsg.toolCalls?.length) {
                  const allFailed = lastMsg.toolCalls.every((tc) => {
                    const result = lastMsg.toolResults?.find((tr) => tr.name === tc.name)
                    if (!result) return true
                    const r = result.result as Record<string, unknown> | null | undefined
                    return r && typeof r === "object" && ("error" in r || (r as { success?: boolean }).success === false)
                  })
                  if (allFailed) return true
                }
                return false
              })() && (
                <div className="flex justify-center">
                  <button
                    onClick={() => chat.retryLast(chatContext)}
                    className="flex items-center gap-1.5 rounded-full border border-border/60 px-3 py-1 text-[11px] text-muted-foreground transition-colors hover:border-border hover:bg-accent hover:text-foreground"
                  >
                    <svg className="size-3" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1.5 2.5v4h4" />
                      <path d="M2.3 10a6 6 0 1 0 .9-4.5L1.5 6.5" />
                    </svg>
                    <span>{t("Try again")}</span>
                  </button>
                </div>
              )}
              <div ref={endRef} />
            </div>
          )}
        </div>
      )}

      {/* Error */}
      {chat.error && (
        <div className="mx-4 mb-2 flex items-center justify-between rounded-lg bg-destructive/5 px-3 py-2">
          <span className="text-[11px] text-destructive">{chat.error}</span>
          <button
            onClick={() => chat.retryLast(chatContext)}
            className="ml-2 rounded-md px-2 py-0.5 text-[11px] font-medium text-destructive hover:bg-destructive/10 transition-colors"
          >
            {t("Retry")}
          </button>
        </div>
      )}

      {/* Input — clean, modern */}
      {!showHistory && (
        <div
          className="px-4 pt-2"
          style={{
            paddingBottom: keyboardOpen
              ? "max(var(--safe-area-bottom, 0px), 4px)"
              : "calc(var(--safe-area-bottom, 0px) + 16px)",
          }}
        >
          {/* Quota reached / no AI on the plan: say so before the user types, with the way out */}
          {quotaNoticeText && (
            <div className="mb-2 rounded-lg bg-amber-500/10 px-3 py-2 text-[11px] text-foreground/80">
              {quotaNoticeText}
            </div>
          )}
          {/* Attachment previews */}
          {attachments.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-2">
              {attachments.map((att, i) => (
                <div
                  key={i}
                  className="flex items-center gap-1.5 rounded-lg border border-border/60 bg-accent/50 px-2 py-1"
                >
                  {att.mimeType.startsWith("image/") ? (
                    <img
                      src={`data:${att.mimeType};base64,${att.data}`}
                      alt={att.name}
                      className="size-6 rounded object-cover"
                    />
                  ) : (
                    <span className="text-[10px]">📎</span>
                  )}
                  <span className="max-w-[100px] truncate text-[11px] text-muted-foreground">
                    {att.name ?? "file"}
                  </span>
                  <button
                    onClick={() => setAttachments((prev) => prev.filter((_, j) => j !== i))}
                    className="ml-0.5 text-muted-foreground/60 hover:text-foreground"
                  >
                    <CrossIcon className="size-2.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-end gap-2 rounded-xl border border-border/60 bg-background px-3 py-2 shadow-sm transition-shadow focus-within:border-border focus-within:shadow-md">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.pdf,.txt,.csv,.json,.md"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={false}
              className="flex size-7 shrink-0 items-center justify-center rounded-lg text-muted-foreground/60 transition-colors hover:text-foreground disabled:opacity-40"
              title="Attach file"
            >
              <PlusIcon className="size-4" />
            </button>
            <textarea
              ref={textareaRef}
              rows={1}
              value={input}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              placeholder={contextHint}
              disabled={false}
              className="max-h-[120px] min-h-[24px] flex-1 resize-none bg-transparent text-sm leading-relaxed outline-none placeholder:text-muted-foreground/60"
            />
            <button
              onClick={chat.isStreaming ? chat.stopStreaming : handleSend}
              disabled={!chat.isStreaming && !input.trim() && attachments.length === 0}
              className={cn(
                "flex size-7 shrink-0 items-center justify-center rounded-lg transition-all",
                chat.isStreaming
                  ? "bg-destructive/10 text-destructive hover:bg-destructive/20"
                  : (input.trim() || attachments.length > 0)
                    ? "bg-primary text-primary-foreground hover:opacity-90"
                    : "text-muted-foreground/40",
              )}
              title={chat.isStreaming ? "Stop generating" : "Send"}
            >
              {chat.isStreaming ? (
                <svg className="size-3.5" viewBox="0 0 16 16" fill="currentColor">
                  <rect x="3" y="3" width="10" height="10" rx="1" />
                </svg>
              ) : (
                <SendIcon className="size-3.5" />
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  )
})

// ---------------------------------------------------------------------------
// Suggestions
// ---------------------------------------------------------------------------

const SUGGESTIONS = [
  "What's on today?",
  "Create a task",
  "Plan a project",
  "What's in inbox?",
]
